import React, { useState } from 'react';
import { KeyItem, ElementType } from './templateSerializer';
import './templateBuilder.css';

interface KeyListPanelProps {
  keys: KeyItem[];
  isLoadingKeys: boolean;
  onAddKeyToCanvas: (key: KeyItem) => void;
  onAddCustomElement: (type: ElementType) => void;
  onAddNewSection: () => void;
}

const KeyListPanel: React.FC<KeyListPanelProps> = ({
  keys,
  isLoadingKeys,
  onAddKeyToCanvas,
  onAddCustomElement,
  onAddNewSection,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'text' | 'photo' | 'multiple'>('all');

  const filteredKeys = keys.filter((k) => {
    const matchesSearch =
      k.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      k.code.toLowerCase().includes(searchTerm.toLowerCase());
    const typeNormalized = (k.keyType || 'text').toLowerCase();
    const matchesTab = activeTab === 'all' || typeNormalized === activeTab;
    return matchesSearch && matchesTab;
  });

  const getKeyTypeBadge = (type: string) => {
    const normalized = (type || 'text').toLowerCase();
    switch (normalized) {
      case 'photo':
        return (
          <span
            className="badge rounded-pill px-2 py-1 me-2 pointer-events-none"
            style={{ backgroundColor: '#10b981', color: '#fff', fontSize: '10px' }}
          >
            <i className="fas fa-image me-1"></i> Photo
          </span>
        );
      case 'multiple':
        return (
          <span
            className="badge rounded-pill px-2 py-1 me-2 pointer-events-none"
            style={{ backgroundColor: '#8b5cf6', color: '#fff', fontSize: '10px' }}
          >
            <i className="fas fa-images me-1"></i> Multiple
          </span>
        );
      case 'text':
      default:
        return (
          <span
            className="badge rounded-pill px-2 py-1 me-2 pointer-events-none"
            style={{ backgroundColor: '#3b82f6', color: '#fff', fontSize: '10px' }}
          >
            <i className="fas fa-font me-1"></i> Text
          </span>
        );
    }
  };

  const handleDragStartKey = (e: React.DragEvent, keyItem: KeyItem) => {
    e.stopPropagation();
    const payload = { type: 'key', keyItem };
    (window as any).__tb_dragged_item = payload;
    if (e.dataTransfer) {
      try {
        const jsonStr = JSON.stringify(payload);
        e.dataTransfer.setData('text/plain', jsonStr);
        e.dataTransfer.setData('application/json', jsonStr);
        e.dataTransfer.effectAllowed = 'copyMove';
      } catch (err) {}
    }
  };

  const handleDragStartCustom = (e: React.DragEvent, elementType: ElementType) => {
    e.stopPropagation();
    const payload = { type: 'custom', elementType };
    (window as any).__tb_dragged_item = payload;
    if (e.dataTransfer) {
      try {
        const jsonStr = JSON.stringify(payload);
        e.dataTransfer.setData('text/plain', jsonStr);
        e.dataTransfer.setData('application/json', jsonStr);
        e.dataTransfer.effectAllowed = 'copyMove';
      } catch (err) {}
    }
  };

  return (
    <div
      className="card border shadow-sm h-100 d-flex flex-column"
      style={{ backgroundColor: '#fafbfc', borderRadius: '14px' }}
    >
      {/* PANEL HEADER */}
      <div className="card-header bg-white border-bottom p-3 flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold mb-0 text-dark d-flex align-items-center">
            <i className="fas fa-key text-primary me-2"></i>
            Project Keys & Elements ({filteredKeys.length})
          </h6>
          <button
            type="button"
            className="tb-btn tb-btn-primary fw-semibold px-3"
            onClick={onAddNewSection}
          >
            <i className="fas fa-plus me-1"></i> New Section
          </button>
        </div>

        {/* QUICK ADD / DRAGGABLE CUSTOM ELEMENTS TOOLBAR */}
        <div className="mt-2 pt-2 border-top">
          <div className="text-uppercase text-secondary fw-semibold mb-2" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
            Draggable Elements (Drag or Click +)
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div
              draggable={true}
              onDragStart={(e) => handleDragStartCustom(e, 'heading')}
              className="tb-btn py-1 px-2 cursor-grab d-flex align-items-center select-none"
              style={{ fontSize: '11px', userSelect: 'none' }}
            >
              <i className="fas fa-grip-vertical text-black-50 me-1" style={{ fontSize: '10px' }}></i>
              <i className="fas fa-heading me-1 text-info"></i> + Header
            </div>
            <div
              draggable={true}
              onDragStart={(e) => handleDragStartCustom(e, 'text')}
              className="tb-btn py-1 px-2 cursor-grab d-flex align-items-center select-none"
              style={{ fontSize: '11px', userSelect: 'none' }}
            >
              <i className="fas fa-grip-vertical text-black-50 me-1" style={{ fontSize: '10px' }}></i>
              <i className="fas fa-align-left me-1 text-secondary"></i> + Custom Text
            </div>
            <div
              draggable={true}
              onDragStart={(e) => handleDragStartCustom(e, 'divider')}
              className="tb-btn py-1 px-2 cursor-grab d-flex align-items-center select-none"
              style={{ fontSize: '11px', userSelect: 'none' }}
            >
              <i className="fas fa-grip-vertical text-black-50 me-1" style={{ fontSize: '10px' }}></i>
              <i className="fas fa-minus me-1 text-muted"></i> + Divider
            </div>
          </div>
        </div>

        {/* SEARCH & FILTER TABS WITH CLEAN GAP */}
        <div className="mt-3">
          <div className="input-group input-group-sm mb-2">
            <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '8px 0 0 8px' }}>
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              style={{ borderRadius: '0 8px 8px 0' }}
              placeholder="Search keys by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="btn btn-outline-secondary border-start-0 bg-white"
                onClick={() => setSearchTerm('')}
              >
                &times;
              </button>
            )}
          </div>

          <div className="d-flex gap-2 w-100">
            <button
              type="button"
              className={`tb-btn flex-fill ${activeTab === 'all' ? 'tb-btn-primary' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All ({keys.length})
            </button>
            <button
              type="button"
              className={`tb-btn flex-fill ${activeTab === 'text' ? 'tb-btn-primary' : ''}`}
              onClick={() => setActiveTab('text')}
            >
              <i className="fas fa-font me-1"></i> Text
            </button>
            <button
              type="button"
              className={`tb-btn flex-fill ${activeTab === 'photo' ? 'tb-btn-primary' : ''}`}
              onClick={() => setActiveTab('photo')}
            >
              <i className="fas fa-image me-1"></i> Photo
            </button>
            <button
              type="button"
              className={`tb-btn flex-fill ${activeTab === 'multiple' ? 'tb-btn-primary' : ''}`}
              onClick={() => setActiveTab('multiple')}
            >
              <i className="fas fa-images me-1"></i> Multiple
            </button>
          </div>
        </div>
      </div>

      {/* KEY LIST CONTAINER - EXACT FIXED SCROLL HEIGHT MATCHING LEFT PANEL */}
      <div className="card-body p-3 overflow-auto" style={{ height: '560px' }}>
        {isLoadingKeys ? (
          <div className="text-center py-5">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
            <span className="text-muted small">Loading project keys...</span>
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-exclamation-circle fa-2x mb-2 text-warning"></i>
            <p className="small mb-1 font-semibold">No keys available for selected project.</p>
            <p className="small text-secondary">
              Please select a project first or add keys in Project Key setup.
            </p>
          </div>
        ) : filteredKeys.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-filter fa-lg mb-2"></i>
            <p className="small mb-0">No keys match search criteria "{searchTerm}".</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {filteredKeys.map((keyItem) => (
              <div
                key={keyItem.id || keyItem.code}
                draggable={true}
                onDragStart={(e) => handleDragStartKey(e, keyItem)}
                className="p-2 border rounded-3 bg-white tb-key-card cursor-grab d-flex align-items-center justify-content-between select-none"
                style={{
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  cursor: 'grab',
                  userSelect: 'none',
                }}
              >
                <div className="d-flex align-items-center gap-2 overflow-hidden me-2 pointer-events-none">
                  <i className="fas fa-grip-vertical text-black-50 me-1 ms-1" style={{ fontSize: '11px' }}></i>
                  {getKeyTypeBadge(keyItem.keyType)}
                  <div className="text-truncate">
                    <div className="fw-semibold text-dark small text-truncate" title={keyItem.name}>
                      {keyItem.name}
                    </div>
                    <code className="text-muted" style={{ fontSize: '11px' }}>
                      {keyItem.code}
                    </code>
                  </div>
                </div>

                <button
                  type="button"
                  className="tb-btn tb-btn-icon tb-btn-primary flex-shrink-0"
                  title="Add to Canvas"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddKeyToCanvas(keyItem);
                  }}
                >
                  <i className="fas fa-plus" style={{ fontSize: '11px' }}></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FOOTER HELPER */}
      <div className="card-footer bg-white border-top p-2 text-center text-muted flex-shrink-0" style={{ fontSize: '11px' }}>
        <i className="fas fa-info-circle me-1 text-info"></i>
        Drag keys & elements (+ Header, + Text, + Divider) directly onto canvas sections.
      </div>
    </div>
  );
};

export default KeyListPanel;
